import { LiveAnnouncer } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Directive, ElementRef, HostBinding, HostListener, inject, OnInit, signal, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { BiTranslateService } from "@globals/bi-translate";
import { CustomValidators } from "@globals/classes/CustomValidators";
import { DayJsSetupHandler } from "@globals/classes/DateJsSetupHandler";
import { minBeforePinCanBeRerequested } from "@globals/classes/global-constants";
import { SharedRouteNames } from "@globals/classes/SharedRouteNames";
import { BiPhoneInputComponent } from "@globals/components/bi-phone-input/bi-phone-input.component";
import { BiCountryId, getLanguageIdByLanguageCode, LanguageCodes } from "@globals/enums/BiLanguageAndCountryId";
import { areStringsEqual, isStringNullOrEmpty, stripHtml } from "@globals/helper-functions";
import { BiHttpErrorResponse } from "@globals/models/BiHttpErrorResponse";
import { AnonymousTokenModel } from "@globals/openapi-models/model/anonymousTokenModel";
import { PageNames } from "@globals/openapi-models/model/pageNames";
import { BiDomService } from "@globals/services/bi-dom.service";
import { BiLocalizationHelperBaseService } from "@globals/services/bi-localization-helper-base.service";
import { SubscribeModuleBaseService } from "@globals/services/bi-subscribe-module-base.service";
import { BiNotificationConfig, BiToastNotificationService, NotificationType } from "@globals/services/bi-toast-notification.service";
import { GoogleCaptchaManagerService } from "@globals/services/google-captcha-manager.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import dayjs from "dayjs";
import { RecaptchaComponent } from "ng-recaptcha-2";
import { debounceTime, distinctUntilChanged, EMPTY, finalize, map, Observable, switchMap, take } from "rxjs";
import { IframeModuleUrlParamsHandlerService } from "../../core/iframe-module-url-params-handler.service";
import { MobileEmailFormValue } from "./MobileEmailFormValue";
import { SubscriptionModuleBaseSharedService } from "./SubscriptionModuleBaseSharedService";

/**
 * Shared, abstract class that is meant to be extended by subscription module main component classes
 */
@UntilDestroy()
@Directive()
export abstract class SubscriptionModuleBase implements OnInit {
  public mobileEmailForm: FormGroup;

  public pinCodeCtrl = new FormControl(null, [Validators.required, Validators.min(1000), Validators.max(9999)]);

  // Flags
  public requestingPinCode: boolean;
  public verifyingPinCode: boolean;
  public showEnterPinArea: boolean;
  public showCaptcha: boolean;
  public pinVerified: boolean;
  public pinValid: boolean;
  public pinInvalid: boolean;
  public showDisclaimer = signal(false);

  public disclaimerText: string;
  public pageNameId: PageNames = PageNames.SubscriptionModule;

  public showSubscribeConfirmedText = signal(false);
  public loading = true;
  public customerGuidMissing = false;

  protected isNameRequired: boolean;
  /**
   * The DayJs date time at which user requested a PIN code
   */
  protected lastPinRequestDateTime: dayjs.Dayjs;
  protected lastPhoneNumberUsed: string;
  protected lastEmailUsed: string;
  public subHeaderTranslationKey = signal<string>(undefined);
  private anonymousAccessToken: string;

  /**
   * Step in the subscription module
   */
  public currentStep = signal<1 | 2 | 3>(1);

  @ViewChild("pinInput") public pinInput: ElementRef;
  @ViewChild("phoneInputVoice") public phoneInputVoice?: BiPhoneInputComponent;
  @ViewChild("phoneInput") public phoneInput: BiPhoneInputComponent;
  @ViewChild("recaptcha") public recaptchaComponent: RecaptchaComponent;

  @HostBinding("class.iframe-component") public get hostClass() {
    return true;
  }

  /**
   * Binding for the native browser's "keypress" event where we can listen for press on Enter.
   */
  @HostListener("keypress", ["$event"]) public onKeyPress(e: KeyboardEvent) {
    // if focus is on the reset button, then perform reset on Enter
    if (e.key === "Enter") {
      const activeElClasses = document.activeElement.className;
      if (activeElClasses.indexOf("reset-button") !== -1) this.onReset();
      else if (activeElClasses.indexOf("disclaimer-link") !== -1) this.showDisclaimer.set(true);
      else if (activeElClasses.indexOf("close-icon-button") !== -1) this.showDisclaimer.set(false);
    }
  }

  public readonly localizor = inject(BiLocalizationHelperBaseService);
  private readonly router = inject(Router);
  protected readonly sharedService: SubscriptionModuleBaseSharedService;
  protected readonly announcer = inject(LiveAnnouncer);
  protected readonly titleService = inject(Title);
  private readonly document = inject(DOCUMENT);
  protected readonly domService = inject(BiDomService);
  private readonly googleCaptchaManager = inject(GoogleCaptchaManagerService);
  private readonly urlParamsHandler = inject(IframeModuleUrlParamsHandlerService);

  protected readonly translator = inject(BiTranslateService);
  protected readonly cd = inject(ChangeDetectorRef);
  protected readonly notifier = inject(BiToastNotificationService);
  protected readonly subscriptionService = inject(SubscribeModuleBaseService);

  constructor(sharedService: SubscriptionModuleBaseSharedService) {
    this.sharedService = sharedService;
    // Dynamically load necessary styles here. The bundles are configured inside angular.json to NOT be auto-injected.
    this.domService.loadStyle("subscription-module-styles.css");
    this.domService.loadStyle("primeIcons.css");
    this.domService.loadStyle("ngx-toastr.css");
  }

  public async ngOnInit() {
    await DayJsSetupHandler.setDayJsLocaleAndTimezone(getLanguageIdByLanguageCode(document.documentElement.lang as LanguageCodes));

    // In order to show a scroll bar if the iFrame isn't hight enough for all content
    document.body.style.overflowY = "auto";

    this.getIndexHtmlTitle()
      .pipe(untilDestroyed(this))
      .subscribe(title => this.titleService.setTitle(title));

    // First get URL params
    this.sharedService
      .getAndSetUrlParams()
      .pipe(
        untilDestroyed(this),
        switchMap(() => {
          // The customerId parameter must be present - otherwise inform user by setting flag true
          if (isStringNullOrEmpty(this.sharedService.urlParams.customerId)) {
            this.customerGuidMissing = true;
            return EMPTY;
          }
          return this.sharedService.initModuleConfigObject(
            this.sharedService.urlParams.customerId,
            this.sharedService.urlParams.language ? this.localizor.getLanguageIdByLanguageCode(this.sharedService.urlParams.language) : undefined
          );
        })
      )
      .subscribe({
        next: (model: any) => {
          this.configureLanguage();
          this.urlParamsHandler.processSubscriptionModuleUrlParams(this.sharedService.urlParams);
          this.initMobileEmailForm();
          this.anonymousAccessToken = model.accessToken;
          this.loading = false;
          this.cd.markForCheck();
        },
        error: err => this.checkForCustomerError(err)
      });
  }

  private configureLanguage() {
    let language2Set = this.sharedService.urlParams.language;
    // Set language if set eksplicitly via URL parameter
    if (isStringNullOrEmpty(language2Set)) {
      switch (this.sharedService.countryId) {
        case BiCountryId.DK:
          language2Set = "da";
          break;
        case BiCountryId.SE:
          language2Set = "sv";
          break;
        case BiCountryId.FI:
          language2Set = "fi";
          break;
        case BiCountryId.EN:
          language2Set = "en";
          break;
        case BiCountryId.NO:
          language2Set = "no";
          break;
      }
    }

    this.localizor.setLanguage(language2Set === "sv" ? "se" : language2Set);
    this.document.documentElement.lang = language2Set === "se" ? "sv" : language2Set;
  }

  /**
   * Resets all flags and values
   */
  public onReset() {
    // Make sure that user can't reset last entered info by just clicking reset on first page
    if (this.currentStep() !== 1) {
      this.lastEmailUsed = undefined;
      this.lastPhoneNumberUsed = undefined;
      this.lastPinRequestDateTime = undefined;
    }

    this.showEnterPinArea = false;
    this.showCaptcha = false;
    this.currentStep.set(1);
    this.requestingPinCode = false;
    this.verifyingPinCode = false;
    this.pinInvalid = undefined;
    this.pinValid = undefined;
    this.pinVerified = false;
    this.pinCodeCtrl.reset();
    this.sharedService.setSubscriberInfo({});
    this.mobileEmailForm.reset(undefined, { emitEvent: false });
    this.mobileEmailForm.markAsPristine();
    this.mobileEmailForm.markAsUntouched();
  }

  protected initMobileEmailForm() {
    const mobileRequired = this.sharedService.urlParams.hideEmailInput && !+this.sharedService.urlParams.showLandline;

    this.mobileEmailForm = new FormGroup({
      mobile: new FormControl(null, mobileRequired ? Validators.required : undefined),
      email: new FormControl("", CustomValidators.email()),
      name: new FormControl("", this.isNameRequired ? Validators.required : undefined),
      recaptcha: new FormControl("")
    });

    // add the landline control if URL param true
    if (this.sharedService.urlParams.showLandline) this.mobileEmailForm.addControl("landline", new FormControl(undefined));

    // Use timeout before setting validators in order to not run the validators after form is initialized
    setTimeout(() => {
      if (!this.sharedService.urlParams.hideEmailInput || this.sharedService.urlParams.showLandline) {
        if (!this.sharedService.urlParams.hideEmailInput && !this.sharedService.urlParams.showLandline) {
          this.mobileCtrl.addValidators([CustomValidators.checkAtLeastOneHasValue(this.emailCtrl)]);
          this.emailCtrl.setValidators([CustomValidators.checkAtLeastOneHasValue(this.mobileCtrl), CustomValidators.email()]);
        } else if (this.sharedService.urlParams.hideEmailInput && this.sharedService.urlParams.showLandline) {
          this.mobileCtrl.addValidators([CustomValidators.checkAtLeastOneHasValue(this.landlineCtrl)]);
          this.landlineCtrl.addValidators([CustomValidators.checkAtLeastOneHasValue(this.mobileCtrl)]);
        } else {
          // both email and landline is present
          this.mobileCtrl.addValidators([CustomValidators.checkAtLeastOneHasValue(this.landlineCtrl, this.emailCtrl)]);
          this.landlineCtrl.addValidators([CustomValidators.checkAtLeastOneHasValue(this.mobileCtrl, this.emailCtrl)]);
          this.emailCtrl.setValidators([CustomValidators.checkAtLeastOneHasValue(this.mobileCtrl, this.landlineCtrl), CustomValidators.email()]);
        }
      }

      if (this.sharedService.allowOnlySingleContactInfo) this.setupOnlyOneContactInfoAtATimeValidation();

      this.updateContactInfoCtrlsValueAndValidity();
    }, 0);

    // When the name input is updated, trigger update on the other controls
    this.nameCtrl.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe(() => this.updateContactInfoCtrlsValueAndValidity());

    this.cd.markForCheck();
  }

  private updateContactInfoCtrlsValueAndValidity() {
    this.mobileCtrl.updateValueAndValidity({ onlySelf: true });
    this.landlineCtrl?.updateValueAndValidity({ onlySelf: true });
    this.emailCtrl.updateValueAndValidity({ onlySelf: true });
  }

  /**
   * Small helper for toggling the visibility state of the "enter PIN"-area.
   */
  public toggleEnterPinAreaVisibility() {
    if (this.showEnterPinArea) {
      this.showEnterPinArea = false;
      this.mobileEmailForm.enable();
      this.pinCodeCtrl.reset();
    } else {
      this.showEnterPinArea = true;
      this.mobileEmailForm.disable();
    }
  }

  //#region FORMCONTROL GETTERS
  public get mobileCtrl() {
    return this.mobileEmailForm.get("mobile");
  }
  public get landlineCtrl() {
    return this.mobileEmailForm.get("landline");
  }
  public get emailCtrl() {
    return this.mobileEmailForm.get("email");
  }
  public get nameCtrl() {
    return this.mobileEmailForm.get("name");
  }
  public get recaptchaCtrl() {
    return this.mobileEmailForm.get("recaptcha");
  }
  //#endregion

  /**
   * Processes the url params provided
   */
  protected processUrlParams() {
    this.urlParamsHandler.processSubscriptionModuleUrlParams(this.sharedService.urlParams);
  }

  /**
   * Must return this title to be used for the title tag in index.html
   */
  protected getIndexHtmlTitle() {
    return this.translator.onLangChange.pipe(map(() => this.translator.instant("subscriptionModule.IndexTitle")));
  }

  //#region EVENTHANDLERS

  /**
   * callback for when user either hits OK or "Send PIN kode" - depending on whether PIN-authentiation is
   * enabled or not
   */
  public onOkOrSendCodeClicked() {
    if (this.mobileEmailForm.invalid) {
      this.mobileCtrl.markAsDirty();
      this.emailCtrl.markAsDirty();
      this.nameCtrl.markAsDirty();
      return;
    }

    let accessToken: string = this.anonymousAccessToken;
    const landline = this.phoneInputVoice?.getValueWithCode();
    const mobile = this.phoneInput.getValueWithCode();

    this.sharedService.setSubscriberInfo({
      mobileNumber: this.phoneInput.getValueWithCode(),
      landlineNumber: this.phoneInputVoice?.getValueWithCode(),
      email: this.emailCtrl.value,
      name: this.nameCtrl.value
    });

    if (window.sessionStorage.getItem("accessTokenModel")) {
      accessToken = JSON.parse(window.sessionStorage.getItem("accessTokenModel")).accessToken;
    }

    // disableAuth is set, we need to issue a token, without authentication the user
    if (this.sharedService.urlParams.disableAuth && Number(this.sharedService.urlParams.disableAuth) === 1) {
      accessToken = this.sharedService.urlParams.customerId;
    }

    // Did user re-request a PIN for same number and/or email within some minutes? Then don't do anything but inform user
    if (this.lastPinRequestDateTime && this.lastPinRequestDateTime.diff(dayjs(), "m") < minBeforePinCanBeRerequested) {
      if (
        (!this.lastPhoneNumberUsed && this.lastEmailUsed && areStringsEqual(this.lastEmailUsed, this.emailCtrl.value)) ||
        (this.lastPhoneNumberUsed && areStringsEqual(this.lastPhoneNumberUsed, mobile)) ||
        (this.phoneInputVoice && this.lastPhoneNumberUsed && areStringsEqual(this.lastPhoneNumberUsed, landline))
      )
        this.notifier.createNotification(
          new BiNotificationConfig(
            NotificationType.WARNING,
            this.translator.instant("iFrame.Subscription.PinAlreadySent", {
              minutes: minBeforePinCanBeRerequested
            })
          )
        );
      return;
    }

    this.requestingPinCode = true;

    const pinCodeObservable = this.subscriptionService.requestPinCodeAttempt({
      customerGuid: this.sharedService.urlParams.customerId,
      urlParams: this.sharedService.urlParams,
      pageNameId: this.pageNameId,
      phoneNumber: mobile,
      landline: landline,
      email: this.emailCtrl.value,
      accessToken
    });

    let observable$: Observable<any>;

    // If captcha is active, we must validate on server first
    if (this.showCaptcha) {
      observable$ = this.googleCaptchaManager.validateGoogleCaptchaToken(this.pageNameId).pipe(
        switchMap(isValid => {
          if (isValid) return pinCodeObservable;
          else {
            this.notifier.createNotification(new BiNotificationConfig(NotificationType.WARNING, this.translator.instant("shared.ErrorInCaptcha")));
            this.recaptchaComponent.reset();
            this.mobileEmailForm.reset();
            return EMPTY;
          }
        }),
        untilDestroyed(this),
        take(1)
      );
    } else observable$ = pinCodeObservable.pipe(untilDestroyed(this), take(1));

    // Reset the date of PIN code request and keep track of the last email and number used
    this.lastPinRequestDateTime = dayjs();
    this.lastEmailUsed = this.emailCtrl.value;
    this.lastPhoneNumberUsed = landline ?? mobile;
    observable$
      .pipe(
        finalize(() => {
          this.requestingPinCode = false;
          this.cd.detectChanges();
        })
      )
      .subscribe({
        next: (token: AnonymousTokenModel) => {
          this.showCaptcha = false;
          this.recaptchaCtrl.clearValidators();
          this.recaptchaCtrl.reset();

          if (token) {
            this.sharedService.setSubscriberInfo({ ...this.sharedService.getCurrentSubscriberInfo(), accessToken: token.accessToken });
            this.currentStep.set(2);
            this.cd.detectChanges();
            return;
          }
          this.toggleEnterPinAreaVisibility();
          setTimeout(() => {
            this.pinCodeCtrl.markAsPristine();
            (this.pinInput.nativeElement as HTMLInputElement).focus();
          }, 0);
        },
        error: err => this.handlePinRequestError(err)
      });
  }

  public onVerifyPinCode() {
    if (this.pinCodeCtrl.invalid) {
      this.pinCodeCtrl.markAsDirty();
      return;
    }
    this.announcer.clear();

    this.verifyingPinCode = true;
    this.pinValid = false;
    this.pinInvalid = false;
    this.subscriptionService
      .verifyPinCode(
        this.sharedService.urlParams.customerId,
        this.pageNameId,
        this.pinCodeCtrl.value,
        this.phoneInput.getValueWithCode(),
        this.landlineCtrl && !isStringNullOrEmpty(this.landlineCtrl.value) ? this.phoneInputVoice.getValueWithCode() : null,
        this.emailCtrl.value
      )
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.verifyingPinCode = false;
          this.cd.detectChanges();
        })
      )
      .subscribe({
        next: token => {
          this.pinValid = true;
          this.pinVerified = true;
          this.sharedService.setSubscriberInfo({ ...this.sharedService.getCurrentSubscriberInfo(), accessToken: token.accessToken });

          // PIN succesfully verified - now go get subscription overview data
          this.currentStep.set(2);
          this.toggleEnterPinAreaVisibility();
          this.announcer.announce(this.translator.instant("shared.Verified"));

          this.sharedService.refreshData().subscribe(() => this.cd.detectChanges());
        },
        error: (error: HttpErrorResponse) => {
          this.verifyingPinCode = false;
          this.pinValid = false;
          if (error.status == 429) {
            this.notifier.createNotification(
              new BiNotificationConfig(
                NotificationType.WARNING,
                this.translator.instant("iFrame.Subscription.PinAttemptedTooManyTimes", {
                  minutes: minBeforePinCanBeRerequested
                })
              )
            );
            this.toggleEnterPinAreaVisibility();
          } else {
            this.pinInvalid = true;
            this.pinVerified = true;
            this.announcer.announce(this.translator.instant("shared.InvalidPin"));
          }
        }
      });
  }

  /**
   * Fired after user finished captcha program
   */
  public onCaptchaResolved(responseToken: string) {
    this.googleCaptchaManager.setResponseToken(responseToken);
    this.lastEmailUsed = undefined;
    this.lastPhoneNumberUsed = undefined;
    this.lastPinRequestDateTime = undefined;
    this.announcer.announce(this.translator.instant("googleCaptcha.Verified"));
  }

  public onSkipToMainContentClicked() {
    this.announcer.announce(this.translator.instant(this.subHeaderTranslationKey()));
  }

  //#endregion

  protected handlePinRequestError(err: HttpErrorResponse) {
    // check error type and act accordingly
    if (err.status === 429) {
      // HTTP 429 = too many requests. Use captcha
      this.notifier.createNotification(new BiNotificationConfig(NotificationType.WARNING, undefined, this.translator.instant("iFrame.Subscription.PleaseUseCaptcha")));
      this.showCaptcha = true;
      this.announcer.announce(this.translator.instant("googleCaptcha.MarkNotRobot"));

      this.recaptchaCtrl.setValidators(Validators.required);
    } else if (err.error.errorMessage === "Invalid") {
      this.notifier.createNotification(new BiNotificationConfig(NotificationType.WARNING, undefined, this.translator.instant("iFrame.Subscription.InvalidPleaseReload")));
      this.announcer.announce(this.translator.instant("iFrame.Subscription.InvalidPleaseReload"));
    } else {
      throw err;
    }
  }

  /**
   * Finds out if an HTTP error has occurred because the customer wasn't found and sets
   * error flag accordingly - if it wasn't the case, the error is just thrown to global handler
   */
  protected checkForCustomerError(error: BiHttpErrorResponse) {
    if (error.error?.errorMessage.indexOf("customer") !== -1) {
      this.router.navigate([SharedRouteNames.unavailable], {
        state: { message: this.translator.instant("iFrame.ModuleNotActive") }
      });
      return EMPTY;
    } else {
      throw error;
    }
  }

  protected setupOnlyOneContactInfoAtATimeValidation() {
    this.mobileEmailForm.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged(), debounceTime(300)).subscribe((formData: MobileEmailFormValue) => {
      if (!isStringNullOrEmpty(formData.mobile)) {
        this.landlineCtrl?.reset("", { emitEvent: false });
        this.landlineCtrl?.disable({ emitEvent: false });
        this.emailCtrl.reset("", { emitEvent: false });
        this.emailCtrl.disable({ emitEvent: false });
      } else if (!isStringNullOrEmpty(formData.email)) {
        this.mobileCtrl.reset("", { emitEvent: false });
        this.mobileCtrl.disable({ emitEvent: false });
        this.landlineCtrl?.reset("", { emitEvent: false });
        this.landlineCtrl?.disable({ emitEvent: false });
      } else if (!isStringNullOrEmpty(formData.landline)) {
        this.mobileCtrl.reset("", { emitEvent: false });
        this.mobileCtrl.disable({ emitEvent: false });
        this.emailCtrl?.reset("", { emitEvent: false });
        this.emailCtrl?.disable({ emitEvent: false });
      } else {
        this.mobileCtrl.enable({ emitEvent: false });
        this.landlineCtrl?.enable({ emitEvent: false });
        this.emailCtrl.enable({ emitEvent: false });
      }
    });
  }

  public onShowDisclaimerChanged(show: boolean) {
    this.showDisclaimer.set(show);
    if (show) this.announcer.announce(stripHtml(this.sharedService.disclaimerText));
    else {
      this.announcer.clear();
      document.getElementById("show-disclaimer-text-button").focus(<any>{ focusVisible: true }); // for accessibility!
    }
  }
}
